import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  Switch,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExtensionIcon from '@mui/icons-material/Extension';
import SaveIcon from '@mui/icons-material/Save';
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';
import TextField from '@mui/material/TextField';
import SuperAdminService from '../../services/SuperAdminService';
import { MODULE_DEFINITIONS, ALL_MODULE_IDS, type ModuleId } from '../../config/moduleAccess';

interface ModuleSettingsDialogProps {
  open: boolean;
  onClose: () => void;
  company: { id: string; name: string } | null;
}

/** ISO/Date değerini `<input type="date">` formatına (YYYY-MM-DD, yerel) çevirir. */
const toDateInputValue = (iso: string | null): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

/** YYYY-MM-DD değerini o günün SONU (yerel 23:59) olarak ISO'ya çevirir — gün boyu erişim kalsın. */
const dateInputToIso = (value: string): string | null => {
  if (!value) return null;
  const d = new Date(`${value}T23:59:00`);
  return isNaN(d.getTime()) ? null : d.toISOString();
};

/**
 * SuperAdmin/bayi — şirket bazlı modül aç/kapa + demo süresi diyaloğu.
 * "Tüm modüller açık" (varsayılan, EnabledModulesJson=null) veya seçili modül listesi.
 * Demo: DemoUntil tarihi; null = demo değil. Süre dolunca backend modülleri kilitler.
 */
const ModuleSettingsDialog: React.FC<ModuleSettingsDialogProps> = ({ open, onClose, company }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  /** null = kısıtlama yok (tüm modüller açık) */
  const [modules, setModules] = useState<string[] | null>(null);
  /** ISO tarih; null = demo değil (süresiz). */
  const [demoUntil, setDemoUntil] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (!open || !company) return;
    setError(null);
    setSuccess(null);
    setDirty(false);
    setLoading(true);
    SuperAdminService.getInstance()
      .getCompanyModules(company.id)
      .then((info) => {
        setModules(info.modules);
        setDemoUntil(info.demoUntil);
      })
      .catch((e) => setError(e?.message || 'Modül bilgisi alınamadı'))
      .finally(() => setLoading(false));
  }, [open, company]);

  if (!company) return null;

  const allEnabled = modules === null;

  // Demo durumu (görsel amaçlı yerel hesap; asıl kilit kararı sunucudadır)
  const demoDate = demoUntil ? new Date(demoUntil) : null;
  const demoIsExpired = demoDate !== null && demoDate.getTime() <= Date.now();
  const demoDaysLeft = demoDate
    ? Math.ceil((demoDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24))
    : null;

  /** Hızlı uzatma: mevcut tarihten (geçmişse bugünden) +gün. */
  const handleExtendDemo = (days: number) => {
    setDirty(true);
    setSuccess(null);
    const base = demoDate && demoDate.getTime() > Date.now() ? demoDate : new Date();
    const next = new Date(base.getTime() + days * 24 * 60 * 60 * 1000);
    setDemoUntil(next.toISOString());
  };

  const handleDemoDateChange = (value: string) => {
    setDirty(true);
    setSuccess(null);
    setDemoUntil(dateInputToIso(value));
  };

  const handleRemoveDemo = () => {
    setDirty(true);
    setSuccess(null);
    setDemoUntil(null);
  };

  const handleAllEnabledToggle = (checked: boolean) => {
    setDirty(true);
    setSuccess(null);
    // Kısıtlamaya geçerken mevcut durumu koru: tümü açıktan geçişte hepsi seçili başlar.
    setModules(checked ? null : [...ALL_MODULE_IDS]);
  };

  const handleModuleToggle = (id: ModuleId, checked: boolean) => {
    setDirty(true);
    setSuccess(null);
    setModules((prev) => {
      const current = prev ?? [...ALL_MODULE_IDS];
      return checked ? Array.from(new Set([...current, id])) : current.filter((m) => m !== id);
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      await SuperAdminService.getInstance().setCompanyModules(company.id, modules, demoUntil);
      setSuccess('Modül ve demo ayarları kaydedildi.');
      setDirty(false);
    } catch (e: any) {
      setError(e?.message || 'Modül ayarları kaydedilemedi');
    } finally {
      setSaving(false);
    }
  };

  const enabledCount = modules?.length ?? ALL_MODULE_IDS.length;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 6 }}>
        <ExtensionIcon sx={{ color: '#0288d1' }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h6" component="div" noWrap>
            Modül Yönetimi
          </Typography>
          <Typography variant="body2" color="text.secondary" noWrap>
            {company.name}
          </Typography>
        </Box>
        <Chip
          size="small"
          label={allEnabled ? 'Tümü açık' : `${enabledCount}/${ALL_MODULE_IDS.length} modül açık`}
          color={allEnabled ? 'success' : 'primary'}
          variant="outlined"
        />
        <IconButton onClick={onClose} sx={{ position: 'absolute', top: 8, right: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <>
            <FormControlLabel
              control={
                <Switch
                  checked={allEnabled}
                  onChange={(e) => handleAllEnabledToggle(e.target.checked)}
                  color="success"
                />
              }
              label={
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Tüm modüller açık (varsayılan)
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Kapatırsan aşağıdan şirketin satın aldığı modülleri seçebilirsin.
                  </Typography>
                </Box>
              }
              sx={{ mb: 1 }}
            />

            <Divider sx={{ my: 1.5 }} />

            <Box sx={{ opacity: allEnabled ? 0.45 : 1, pointerEvents: allEnabled ? 'none' : 'auto' }}>
              {MODULE_DEFINITIONS.map((def) => {
                const checked = allEnabled || (modules?.includes(def.id) ?? false);
                return (
                  <Box
                    key={def.id}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      py: 0.75,
                      px: 1,
                      borderRadius: 1,
                      '&:hover': { bgcolor: 'action.hover' },
                    }}
                  >
                    <Box sx={{ minWidth: 0, mr: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {def.labelTr}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {def.descriptionTr}
                      </Typography>
                    </Box>
                    <Switch
                      size="small"
                      checked={checked}
                      onChange={(e) => handleModuleToggle(def.id, e.target.checked)}
                    />
                  </Box>
                );
              })}
            </Box>

            {!allEnabled && enabledCount === 0 && (
              <Alert severity="warning" sx={{ mt: 1.5 }}>
                Hiçbir modül seçili değil — şirket kullanıcıları girişte hiçbir menü göremez.
              </Alert>
            )}

            <Divider sx={{ my: 2 }} />

            {/* ── Demo Kullanım Sınırı ─────────────────────────────────── */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <HourglassBottomIcon fontSize="small" sx={{ color: '#ed6c02' }} />
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Demo Kullanım Sınırı
              </Typography>
              {demoUntil ? (
                <Chip
                  size="small"
                  label={
                    demoIsExpired
                      ? 'Demo doldu'
                      : `Demo — ${Math.max(0, demoDaysLeft ?? 0)} gün kaldı`
                  }
                  color={demoIsExpired ? 'error' : 'warning'}
                />
              ) : (
                <Chip size="small" label="Demo değil (süresiz)" color="success" variant="outlined" />
              )}
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
              Tarih geçtiğinde tüm modüller otomatik kapanır ve kullanıcılar kilit ekranı görür.
              Boş bırakmak demoyu kaldırır (kalıcı müşteri).
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <TextField
                type="date"
                size="small"
                label="Demo bitiş tarihi"
                value={toDateInputValue(demoUntil)}
                onChange={(e) => handleDemoDateChange(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ width: 190 }}
              />
              <Button size="small" variant="outlined" onClick={() => handleExtendDemo(7)}>
                +7 gün
              </Button>
              <Button size="small" variant="outlined" onClick={() => handleExtendDemo(14)}>
                +14 gün
              </Button>
              <Button size="small" variant="outlined" onClick={() => handleExtendDemo(30)}>
                +30 gün
              </Button>
              {demoUntil && (
                <Button size="small" color="error" onClick={handleRemoveDemo}>
                  Demoyu kaldır
                </Button>
              )}
            </Box>

            {demoIsExpired && (
              <Alert severity="error" sx={{ mt: 1.5 }}>
                Demo süresi dolmuş — şirket kullanıcıları şu anda kilit ekranı görüyor.
                Tarihi uzatın veya demoyu kaldırın.
              </Alert>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button onClick={onClose} color="inherit">
          Kapat
        </Button>
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
          onClick={handleSave}
          disabled={saving || loading || !dirty}
        >
          Kaydet
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ModuleSettingsDialog;
